import Controller from '@ember/controller';
import { action } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { Disclaimer, formatMoney, ToggleButton } from '@repo/design-system-kit';
import {
  mapPricePlanProps,
  PricePlanContainerSkeleton,
  PricePlansContainerBridge,
  ToggleButtonRecurrence,
} from '@repo/pricing-kit/components';
import { dropTask } from 'ember-concurrency';

import { MainLayout } from 'register-partners-js/react/components/layouts/main';
import { ErrorInfo } from 'register-partners-js/utils/error-info';

import styles from './styles.module.css';

export default class RegistrationPricePlanController extends Controller {
  mainLayout = MainLayout;

  toggleButtonRecurrence = ToggleButtonRecurrence;
  toggleButtonDesktop = ToggleButton;
  pricePlansContainer = PricePlansContainerBridge;
  pricePlansContainerSkeleton = PricePlanContainerSkeleton;
  disclaimerBlock = Disclaimer.Block;

  @service intl;
  @service router;
  @service segment;
  @service pricingService;
  @service checkout;
  @service sentry;
  @service toastFlashMessages;

  @alias('model.registration') registration;
  @alias('model.organization') organization;

  queryParams = ['lineup', 'recurrence'];

  @tracked lineup = 'solo';
  @tracked recurrence = 'annual';
  @tracked selectedPlan;
  @tracked banner = null;

  @action
  selectRecurrence(recurrence) {
    this.recurrence = recurrence;
    this.segment.track('onboarding_recurrence_toggle_clicked', {
      recurrence: this.computedRecurrence,
    });
  }

  @action
  selectLineup(value) {
    let previousLineup = this.lineup;
    this.lineup = value;
    this.segment.track('onboarding_pricing_lineup_switch', {
      lineup: `${previousLineup}_to_${this.lineup}`,
      from_lineup: previousLineup,
      to_lineup: this.lineup,
    });
  }

  get isPeriodMonthly() {
    return this.computedRecurrence === 'monthly';
  }

  async onPricePlanPressCta(code, allPricePlans) {
    try {
      this.selectedPlan = code;
      await this.savePricePlansTask.perform(allPricePlans.find(p => p.code === code));

      this.segment.track('onboarding_partner_plan_chosen', {
        original_price_plan_code: code,
        recurrence: this.computedRecurrence,
      });

      this.router.transitionTo('registration.order-summary');
    } catch (error) {
      if (ErrorInfo.for(error).shouldSendToSentry) {
        this.sentry.captureException(error);
      }
      this.toastFlashMessages.toastError(this.intl.t('errors.internal-server-error'));
    } finally {
      this.selectedPlan = undefined;
    }
  }

  get computedRecurrence() {
    if (this.model.registration.partnerName.toLowerCase() === 'mollie') {
      return 'monthly';
    }
    return this.recurrence;
  }

  get shouldDisplayRecurrence() {
    return this.model.registration.partnerName.toLowerCase() !== 'mollie';
  }

  get shouldDisplayLineups() {
    return this.model.registration.partnerName.toLowerCase() !== 'mollie';
  }

  get shouldDisplayComparePlans() {
    return this.model.registration.partnerName.toLowerCase() !== 'mollie';
  }

  get hasPromoOffers() {
    let allPricePlans = this.getPricePlansTask.lastSuccessful?.value;
    if (allPricePlans) {
      return allPricePlans.some(plan => {
        return plan.monthly_price.promo_offer || plan.annual_price.promo_offer;
      });
    }
  }

  get promoBanner() {
    if (this.hasPromoOffers) {
      return this.banner;
    }
  }

  get pricePlans() {
    let allPricePlans = this.getPricePlansTask.lastSuccessful?.value;
    let codeLineup = this.lineup === 'teams' ? 'team' : 'solo';

    let { pricePlans } = mapPricePlanProps(
      this.shouldDisplayLineups
        ? allPricePlans?.filter(plan => plan.code.startsWith(codeLineup))
        : allPricePlans,
      {
        isCompanyCreation: false,
        country: this.organization.get('legalCountry'),
        isSubmitting: this.savePricePlansTask.isRunning,
        mainButton: {
          text: this.intl.t('generic.choose.cta'),
          onPress: code => this.onPricePlanPressCta(code, allPricePlans),
        },
        addOnsTitle: this.intl.t('price_plan.add-ons.title'),
        promoOfferBadge: this.intl.t('badges.limited-time'),
        selectedPlan: this.selectedPlan,
        recurrence: this.computedRecurrence,
        formatPrice: amount =>
          formatMoney({
            amount,
            locale: this.intl.primaryLocale,
            token: 'money-symbol-compact',
          }),
        descriptionCta: {
          type: 'button',
          onPress: () => {
            this.selectRecurrence('annual');
          },
        },
        hidePromoInAllProducts: false,
        hideOfferDetails: true,
      }
    );

    return {
      pricePlans,
      hidePromoInAllProducts: !this.hasPromoOffers,
      hasPromoInAnyProducts: this.hasPromoOffers,
    };
  }

  @action
  handleGoBack() {
    this.router.transitionTo(
      'react',
      'registrations/' + this.model.registration.id + '/owner-info'
    );
  }

  get getTabItems() {
    return [
      {
        value: 'solo',
        label: this.intl.t(
          'existing-company.fast-track.price-plans-screen.self-employed-tab.title'
        ),
      },
      {
        value: 'teams',
        label: this.intl.t('existing-company.fast-track.price-plans-screen.teams-tab.title'),
      },
    ];
  }

  get hasAddOns() {
    return this.lineup !== 'solo';
  }

  getPricePlansTask = dropTask(async ({ legalCountry, partnerName }) => {
    let { products, banner } = await this.checkout.fetchPricePlansForExistingCompanyTask.perform({
      legalCountry,
      partnerName,
    });
    this.banner = banner;

    return products;
  });

  savePricePlansTask = dropTask(async plan => {
    await this.checkout.savePricePlan({
      organizationId: this.organization.get('id'),
      planId: plan.id,
      planCode: plan.code,
      recurrence: this.isPeriodMonthly ? 'monthly' : 'annual',
      flow: 'existing_company',
      legalCountry: this.organization.get('legalCountry'),
      partnerName: this.registration.partnerName?.toLowerCase(),
    });
  });
  styles = styles;
}
